/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Paper,
  IconButton,
  Grid,
  Button,
  Badge,
  Breadcrumbs,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { GlossaryDialog } from '../components/glossary/GlossaryCollectionsDialog';
import { getStore, RootState } from '@ink-ai/portal/reducers';
import {
  glossaryExtractionActions,
  handleDownload,
  uploadAndExtractGlossary,
} from '@ink-ai/portal/reducers/glossary-extraction';
import { app } from '@ink-ai/portal/reducers/app';
import { LanguageTabs } from '../components/extraction/LanguageTabs';
import { FileUploadArea, GlossaryExtractionArea } from '../components/extraction/GlossaryExtractionArea';
import { GlossaryExtractionOptionDtoLanguagesEnum } from '@ink-ai/insight-service-sdk';
import { compose } from '@ink-ai/portal/reducers/compose';
import { Link, useBlocker, useNavigate } from 'react-router-dom';
import ExtractionIcon from '@mui/icons-material/ManageSearch';

const confirmExit = () => {
  const glossaryExtraction = getStore().getState().glossaryExtraction;
  if (glossaryExtraction.glossaryExtractionStatus === 'uploading') {
    return 'The document is still in the process of uploading. Are you sure, you want to close?';
  }
};

export const GlossaryExtraction: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const store = getStore();
  const composeState = useSelector((state: RootState) => state.compose);
  const glossaryExtraction = useSelector((state: RootState) => state.glossaryExtraction);
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      glossaryExtraction.glossaryExtractionStatus === 'uploading' &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    window.onbeforeunload = confirmExit;
    return () => {
      window.onbeforeunload = null;
    };
  }, []);

  useEffect(() => {
    dispatch(glossaryExtractionActions.setSelectedLanguages(['ZH_CN', 'EN_US']));
    // Clean up on unmount
    return () => {
      dispatch(glossaryExtractionActions.clearAll());
      dispatch(compose.actions.clearAll());
    };
  }, []);
  const handleDrop = (event: React.DragEvent<HTMLDivElement>, language: string) => {
    event.preventDefault(); // Prevent the default behavior (e.g., opening the file)
  
    const files = event.dataTransfer.files;
  
    // If files were dropped, pass them to handleFileChange as a simulated ChangeEvent
    if (files.length > 0) {
      // Simulate a ChangeEvent with the dropped files
      const simulatedEvent = {
        target: {
          files: files, // The files from the drop event
        },
      } as React.ChangeEvent<HTMLInputElement>; // Typecast it to the correct event type
  
      // Call handleFileChange with the simulated event and the language
      handleFileChange(simulatedEvent, language);
    }
  };
  
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault(); // Prevent default behavior to allow for a drop event
  };
  

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, language: string) => {
    const file = event.target.files?.[0];
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/pdf',
    ];
  
    if (file && allowedTypes.includes(file.type)) {
      // Only dispatch file-related actions if the file is valid
      dispatch(glossaryExtractionActions.setFileName( file.name ));
      dispatch(glossaryExtractionActions.setSelectedFileForLanguage({ language, file }));
      dispatch(glossaryExtractionActions.setFileNameForLanguage({ language, fileName: file.name }));
      dispatch(glossaryExtractionActions.setFileSizeForLanguage({ language, fileSize: file.size }));
    } else {
      // Clear the selected file if invalid
      dispatch(glossaryExtractionActions.setSelectedFileForLanguage({ language, file: null }));
      store.dispatch(
        app.actions.setGlobalMessage({
          message: 'Invalid file type. Please upload a .docx or .pdf file.',
          status: 'error',
        })
      );
    }
  };
  
  
  const handleExtractGlossary = async () => {
    console.log('Extracting glossary...');
    console.log(glossaryExtraction)
    try {
      dispatch(glossaryExtractionActions.setLoading(true));
      const trimmedName = (name || '').trim();
      if (!trimmedName) {
        setNameError(t('glossary.dialog.form.name.required'));
        dispatch(
          app.actions.setGlobalMessage({
            message: t('glossary.dialog.form.name.required'),
            status: 'error',
          })
        );
        dispatch(glossaryExtractionActions.setLoading(false));
        return;
      }
      // Check if any files are actually selected by checking if the object has any valid file entries
      const hasSelectedFiles = glossaryExtraction.selectedFiles && 
        Object.values(glossaryExtraction.selectedFiles).some(file => file !== null && file !== undefined);

      // Check if two selected files have the same extension when both exist
      const filesArray = Object.values(glossaryExtraction.selectedFiles || {}).filter(Boolean) as File[];
      if (filesArray.length >= 2) {
        const getExt = (f: File) => {
          const name = f.name || '';
          const parts = name.split('.');
          return (parts.length > 1 ? parts.pop() : '')?.toLowerCase();
        };
        const exts = new Set(filesArray.map(getExt));
        if (exts.size > 1) {
          dispatch(
            app.actions.setGlobalMessage({
              message: t('glossaryExtraction.import.sameTypeRequired'),
              status: 'error',
            })
          );
          dispatch(glossaryExtractionActions.setLoading(false));
          return;
        }
      }
      
      if (hasSelectedFiles) {
        await dispatch(uploadAndExtractGlossary({ name: trimmedName, description })).unwrap();
        // Navigate back to the task list after starting extraction successfully
        navigate('/extraction/glossary/tasks');
      } else {
        console.error('No file selected.');
        dispatch(
          app.actions.setGlobalMessage({
            message: 'No file selected.',
            status: 'error',
          })
        );
        dispatch(glossaryExtractionActions.setLoading(false));
      }
    } catch (error) {
      console.error(error);
      const anyErr: any = error;
      const status = anyErr?.status ?? anyErr?.response?.status;
      const backendMessage = anyErr?.message ?? anyErr?.response?.data?.message;
      const messageToShow = status && backendMessage ? backendMessage : (backendMessage || 'Failed to extract glossary');
      dispatch(
        app.actions.setGlobalMessage({
          message: messageToShow,
          status: 'error',
        })
      );
      dispatch(glossaryExtractionActions.setLoading(false));
    }
  };

  const formatFileSize = (size: number) => {
    if (size < 1024) return `${size} bytes`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} kb`;
    if (size < 1024 * 1024 * 1024)
      return `${(size / (1024 * 1024)).toFixed(2)} mb`;
    return `${(size / (1024 * 1024 * 1024)).toFixed(2)} gb`;
  };

  const handleLanguagesChange = (
    languages: GlossaryExtractionOptionDtoLanguagesEnum[],
  ) => {
      dispatch(glossaryExtractionActions.setSelectedLanguages(languages));
  };

  // Auto navigate back to the tasks list when extraction has started or completed
  useEffect(() => {
    const status = glossaryExtraction.glossaryExtractionStatus;
    if (status === 'extracting' || status === 'queueing' || status === 'completed') {
      navigate('/extraction/glossary/tasks');
    }
  }, [glossaryExtraction.glossaryExtractionStatus]);
  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t('glossaryExtraction.navigation.home')}</Link>
        <Link to="/extraction/glossary/tasks">{t('glossaryExtraction.navigation.glossaryExtractionTasks')}</Link>
        <Typography color="textPrimary">{t('glossaryExtraction.navigation.new')}</Typography>
      </Breadcrumbs>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
        {/* <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
          <Grid container alignItems="center" spacing={1}>
            <Grid item xs>
              <LanguageTabs
                languages={glossaryExtraction.languages}
                selectedLanguages={glossaryExtraction.selectedLanguages}
                setSelectedLanguages={handleLanguagesChange}
                disabled={composeState.isGenerating}
              />
            </Grid>
          </Grid>
        </Box> */}
  
        <Typography variant="h5" sx={{ textAlign: 'left' }}>
          {t('glossaryExtraction.import.title')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, textAlign: 'left' }}
        >
          {t('glossaryExtraction.import.description')}
        </Typography>
        <Box sx={{ width: '50%' }}>
        <TextField
          label={t('glossaryExtraction.import.form.name')}
          variant="outlined"
          fullWidth
          sx={{ mb: 2 }}
          value={name}
          InputLabelProps={{
            shrink: true,
          }}
          onChange={(e) => {
            const v = e.target.value;
            setName(v);
            if ((v || '').trim()) setNameError(null);
          }}
          required
          error={!!nameError}
          helperText={nameError || ''}
        />
          <TextField
            label={t('glossaryExtraction.import.form.description')}
            variant="outlined"
            fullWidth
            sx={{ mb: 1 }}
            value={description}
            InputLabelProps={{
              shrink: true,
            }}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Box>

        {/* Map over selectedLanguages and render a FileUploadArea for each */}
        {glossaryExtraction.selectedLanguages.map((language, index) => (
          <FileUploadArea
            key={index}
            glossaryExtraction={glossaryExtraction}
            language={language} // pass the language to each FileUploadArea
            handleDrop={(event) => handleDrop(event, language)}
            handleDragOver={handleDragOver}
            handleFileChange={(event) => handleFileChange(event, language)}
            handleRemoveFile={() => dispatch(glossaryExtractionActions.removeFileForLanguage({language}))}
            formatFileSize={formatFileSize}
          />
        ))}
        <GlossaryExtractionArea
          glossaryExtraction={glossaryExtraction}
          handleExtractGlossary={handleExtractGlossary}
          handleDownload={handleDownload}
        />
      </Paper>
  
      <Dialog open={blocker.state === 'blocked'}>
        <DialogTitle>{t('glossaryExtraction.dialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('glossaryExtraction.dialog.content')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => blocker.proceed()}>
              {t('glossaryExtraction.dialog.leave')}
            </Button>
          <Button
            onClick={() => blocker.reset()}
            color="primary"
            variant="contained"
            autoFocus
          >
            {t('glossaryExtraction.dialog.stay')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );  
};
